import { defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import { VersionForTableViewSchema } from '../shared/schemas/datintellSchema.js';
import {
  AdDeleteRequestSchema,
  AdGroupDeleteRequestSchema,
  AdGroupsResponseSchema,
  AdSettingsSchema,
  AdsResponseSchema,
  AgGridColumnsResponseSchema,
  AgGridEntityRequestSchema,
  CampaignsDeleteRequestSchema,
  CampaignSettingsSchema,
  CampaignsResponseSchema,
  CommonTotalsResponseSchema,
  CurrencyListTypeResponseSchema,
  DeleteTileSettingsResponseSchema,
  ExchangeRateRequestSchema,
  ExchangeRateResponseSchema,
  IntegrationRequestSchema,
  LaunchManualSyncRequestSchema,
  LaunchManualSyncResponseSchema,
  PostTileSettingsRequestSchema,
  PostTileSettingsResponseSchema,
  RequestFilterParams,
  ResponseString,
  SetCurrencyRequestSchema,
  TextFilterCondition,
} from '../shared/schemas/settingsSchema.js';

export const getMediaPlanRowData = defaultClient.query({
  url: 'datintell/v1/builder/media_plans/$mediaPlan/?include_fields=[]' as const,
  method: 'GET',
  responseSchema: VersionForTableViewSchema,
  processResponse(data) {
    return data;
  },
});

export const postTileSettings = defaultClient.mutation({
  url: 'omni-connector-service/tile-settings/' as const,
  method: 'POST',
  requestSchema: PostTileSettingsRequestSchema,
  responseSchema: PostTileSettingsResponseSchema,
  processResponse(data) {
    return data;
  },
});

export const deleteTileSettings = defaultClient.mutation({
  url: 'omni-connector-service/tile-settings/$tileId' as const,
  method: 'DELETE',
  responseSchema: DeleteTileSettingsResponseSchema,
  querySchema: IntegrationRequestSchema,
  processResponse(data) {
    return data;
  },
});

export const getCurrencyListTypes = defaultClient.query({
  url: 'currency/v1/currencies/' as const,
  method: 'GET',
  responseSchema: CurrencyListTypeResponseSchema,
  processResponse(data) {
    return data?.data?.items;
  },
});

export const fetchExchangeRate = defaultClient.mutation({
  url: 'currency/v1/exchange_rates/rate/' as const,
  method: 'POST',
  requestSchema: ExchangeRateRequestSchema,
  responseSchema: ExchangeRateResponseSchema,
  processResponse(data) {
    return data;
  },
});

export const setCurrency = defaultClient.mutation({
  url: 'datintell/v1/builder/media_plan_items/$mediaPlanItemId/add_integration/' as const,
  method: 'POST',
  requestSchema: SetCurrencyRequestSchema,
  responseSchema: ResponseString,
  processResponse(data) {
    return data;
  },
});

export const fetchCampaignsTotals = defaultClient.query({
  url: 'omni-connector-service/$tileSettingsId/campaign/totals' as const,
  method: 'GET',
  responseSchema: CommonTotalsResponseSchema,
  processResponse(data) {
    return data?.data;
  },
});

export const campaignDelete = defaultClient.mutation({
  url: 'omni-connector-service/$tileSettingsId/campaign/delete-bulk' as const,
  method: 'POST',
  responseSchema: ResponseString,
  requestSchema: CampaignsDeleteRequestSchema,
  processResponse(data) {
    return data;
  },
});

export const fetchAgGroupsTotals = defaultClient.query({
  url: 'omni-connector-service/$tileSettingsId/ad-group/totals' as const,
  method: 'GET',
  responseSchema: CommonTotalsResponseSchema,
  processResponse(data) {
    return data?.data;
  },
});

export const adGroupDelete = defaultClient.mutation({
  url: 'omni-connector-service/$tileSettingsId/ad-group/delete-bulk' as const,
  method: 'POST',
  responseSchema: ResponseString,
  requestSchema: AdGroupDeleteRequestSchema,
});

export const adDelete = defaultClient.mutation({
  url: 'omni-connector-service/$tileSettingsId/ad/delete-bulk' as const,
  method: 'POST',
  responseSchema: ResponseString,
  requestSchema: AdDeleteRequestSchema,
});

export const fetchAdColumns = defaultClient.query({
  url: 'omni-connector-service/$tileSettingsId/ad/ag-grid/columns' as const,
  method: 'GET',
  responseSchema: AgGridColumnsResponseSchema,
  querySchema: z.object({
    platform: z.string(),
  }),
  processResponse(data) {
    return data?.data;
  },
});

export const fetchAgGridAds = defaultClient.mutation({
  url: 'omni-connector-service/$tileSettingsId/ad/ag-grid' as const,
  method: 'POST',
  responseSchema: AdsResponseSchema,
  requestSchema: AgGridEntityRequestSchema,
  processResponse(data) {
    return data.data;
  },
});

export const fetchAdGroupsColumns = defaultClient.query({
  url: 'omni-connector-service/$tileSettingsId/ad-group/ag-grid/columns' as const,
  method: 'GET',
  responseSchema: AgGridColumnsResponseSchema,
  querySchema: z.object({
    platform: z.string(),
  }),
  processResponse(data) {
    return data?.data;
  },
});

export const fetchAgGridAdGroups = defaultClient.mutation({
  url: 'omni-connector-service/$tileSettingsId/ad-group/ag-grid' as const,
  method: 'POST',
  responseSchema: AdGroupsResponseSchema,
  requestSchema: AgGridEntityRequestSchema,
  processResponse(data) {
    return data.data;
  },
});

export const fetchCampaignsColumns = defaultClient.query({
  url: 'omni-connector-service/$tileSettingsId/campaign/ag-grid/columns' as const,
  method: 'GET',
  responseSchema: AgGridColumnsResponseSchema,
  querySchema: z.object({
    platform: z.string(),
  }),
  processResponse(data) {
    return data?.data;
  },
});

export const fetchCampaigns = defaultClient.mutation({
  url: 'omni-connector-service/$tileSettingsId/campaign/ag-grid' as const,
  method: 'POST',
  responseSchema: CampaignsResponseSchema,
  requestSchema: AgGridEntityRequestSchema,
  processResponse(data) {
    return data.data;
  },
});

export const launchManualSync = defaultClient.mutation({
  url: 'omni-connector-service/import/manual-sync' as const,
  method: 'POST',
  responseSchema: LaunchManualSyncResponseSchema,
  requestSchema: LaunchManualSyncRequestSchema,
  querySchema: IntegrationRequestSchema,
  processResponse(data) {
    return data;
  },
});

export const toggleAutoSync = defaultClient.mutation({
  url: 'omni-connector-service/import/$tileId/toggle-autosync' as const,
  method: 'POST',
  responseSchema: ResponseString,
  requestSchema: z.object({}),
  processResponse(data) {
    return data;
  },
});

export type CampaignSettingsItem = z.infer<typeof CampaignSettingsSchema>;
export type AdSettingsItem = z.infer<typeof AdSettingsSchema>;

export type TextFilterConditionType = z.infer<typeof TextFilterCondition>;
export type FilterParams = z.infer<typeof RequestFilterParams>;
